import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import UnifiedEditJobModal from './UnifiedEditJobModal';
import SupportModal from './SupportModal';
import EmergencyCallButton from './EmergencyCallButton';
import JobFundingModal from './JobFundingModal';
import CancelJobModal from './CancelJobModal';
import { 
  ArrowLeft,
  MapPin,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  RotateCcw,
  Building,
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  User,
  TrendingUp,
  Edit,
  AlertCircle,
  Lock,
  HelpCircle,
  CreditCard,
  Shield,
  X,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetail = () => {
  const { jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPassBackModal, setShowPassBackModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [passBackReason, setPassBackReason] = useState('');
  const [fundingBannerDismissed, setFundingBannerDismissed] = useState(false);

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  // Handle deep-link query parameters (move after job and permission calculations)
  useEffect(() => {
    if (job && user) {
      const urlParams = new URLSearchParams(window.location.search);
      const canFundCheck = (user.id === job.originator_id || user.is_admin) && 
                          job.status === 'posted' && 
                          (job.payment_status === 'unfunded' || !job.payment_status);
      
      if (urlParams.get('open') === 'fund' && canFundCheck) {
        setShowFundingModal(true);
      }
    }
  }, [job, user]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch job, timeline, commissions, and notifications in parallel
      const [jobResponse, timelineResponse, commissionsResponse, notificationsResponse] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/details`).catch(() => 
          // Fallback to general jobs endpoint if details endpoint fails
          axios.get(`${API}/jobs`).then(response => {
            const foundJob = response.data.find(j => j.id === jobId);
            return foundJob ? { data: foundJob } : Promise.reject(new Error('Job not found'));
          })
        ),
        axios.get(`${API}/jobs/${jobId}/timeline`).catch(() => ({ data: [] })),
        user?.is_admin ? axios.get(`${API}/admin/commissions`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        user?.is_admin ? axios.get(`${API}/notifications`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);

      // Set job data with null-safe handling
      const jobData = jobResponse.data;
      if (!jobData) {
        toast.error('Job not found');
        navigate('/jobs-list');
        return;
      }

      // Ensure all required fields have defaults
      const safeJobData = {
        // Core fields
        id: jobData.id || jobId,
        title: jobData.title || 'Untitled Job',
        status: jobData.status || 'posted',
        originator_id: jobData.originator_id || '',
        originator_business: jobData.originator_business || 'Unknown Business',
        
        // Location fields with null-safe defaults
        location: jobData.location || 'Location not specified',
        location_name: jobData.location_name || null,
        detailed_location: jobData.detailed_location || null,
        gps_coordinates: jobData.gps_coordinates || null,
        
        // Address fields (new)
        location_formatted_address: jobData.location_formatted_address || null,
        location_lat: jobData.location_lat || null,
        location_lng: jobData.location_lng || null,
        location_place_id: jobData.location_place_id || null,
        location_manual_entry: jobData.location_manual_entry || false,
        
        // Detailed address fields (new)  
        detailed_formatted_address: jobData.detailed_formatted_address || null,
        detailed_lat: jobData.detailed_lat || null,
        detailed_lng: jobData.detailed_lng || null,
        detailed_place_id: jobData.detailed_place_id || null,
        detailed_manual_entry: jobData.detailed_manual_entry || false,
        
        // Payment fields with null-safe defaults
        payment_status: jobData.payment_status || 'unfunded',
        payment_intent_id: jobData.payment_intent_id || null,
        escrow_transaction_id: jobData.escrow_transaction_id || null,
        originator_amount: jobData.originator_amount || null,
        receiver_amount: jobData.receiver_amount || null,
        platform_fee: jobData.platform_fee || null,
        commission_model: jobData.commission_model || null,
        funded_at: jobData.funded_at || null,
        released_at: jobData.released_at || null,
        
        // Other fields
        quoted_price: jobData.quoted_price || 0,
        urgency_level: jobData.urgency_level || 'normal',
        job_specification: jobData.job_specification || jobData.tyre_specification || '',
        tyre_specification: jobData.tyre_specification || '',
        industry: jobData.industry || ['Tyre Service'],
        created_at: jobData.created_at,
        accepted_by: jobData.accepted_by || null,
        accepted_by_business: jobData.accepted_by_business || null,
        accepted_at: jobData.accepted_at || null,
        completed_at: jobData.completed_at || null,
        pass_back_reason: jobData.pass_back_reason || null,
        
        // Invoice fields
        originator_invoice_number: jobData.originator_invoice_number || null,
        receiver_invoice_number: jobData.receiver_invoice_number || null,
        
        // Pricing breakdown - ensure it's always an array
        pricing_breakdown: Array.isArray(jobData.pricing_breakdown) 
          ? jobData.pricing_breakdown 
          : (jobData.pricing_breakdown ? [jobData.pricing_breakdown] : []),
        
        // Other detailed fields
        brand_requirements: jobData.brand_requirements || null,
        alternate_brand_offer: jobData.alternate_brand_offer || null,
        special_notes: jobData.special_notes || null,
        client_name: jobData.client_name || null,
        client_contact_name: jobData.client_contact_name || null,
        client_contact_phone: jobData.client_contact_phone || null,
        client_contact_email: jobData.client_contact_email || null
      };
      
      setJob(safeJobData);
      setTimeline(timelineResponse.data || []);
      
      // Filter commissions and notifications for this job
      setCommissions((commissionsResponse.data || []).filter(c => c.job_id === jobId));
      setNotifications((notificationsResponse.data || []).filter(n => n.job_id === jobId));
      
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptJob = async () => {
    try {
      await axios.post(`${API}/jobs/accept`, { job_id: jobId });
      toast.success('Job accepted successfully!');
      fetchJobDetails();
    } catch (error) {
      console.error('Failed to accept job:', error);
      const message = error.response?.data?.detail || 'Failed to accept job';
      toast.error(message);
    }
  };

  const handleCompleteJob = async () => {
    try {
      await axios.post(`${API}/jobs/complete`, { job_id: jobId });
      toast.success('Job completed! Commission calculated.');
      fetchJobDetails();
    } catch (error) {
      console.error('Failed to complete job:', error);
      toast.error('Failed to complete job');
    }
  };

  const handlePassBackJob = async () => {
    if (!passBackReason.trim()) {
      toast.error('Please provide a reason for passing back the job');
      return;
    }

    try {
      await axios.post(`${API}/jobs/pass-back`, { 
        job_id: jobId, 
        reason: passBackReason.trim() 
      });
      toast.success('Job passed back successfully!');
      setShowPassBackModal(false);
      setPassBackReason('');
      fetchJobDetails();
    } catch (error) {
      console.error('Failed to pass back job:', error);
      toast.error('Failed to pass back job');
    }
  };

  const handleReleasePayment = async () => {
    try {
      await axios.post(`${API}/payments/release`, { job_id: jobId });
      toast.success('Payment released successfully! Funds have been distributed.');
      fetchJobDetails();
    } catch (error) {
      console.error('Failed to release payment:', error);
      toast.error('Failed to release payment');
    }
  };

  const calculateCommission = (price) => {
    if (price < 1000) {
      return { originator: 50, platform: 50, receiver: price - 100 };
    } else if (price <= 5000) {
      const fee = price * 0.1;
      return { originator: fee / 2, platform: fee / 2, receiver: price - fee };
    } else {
      const originator = price * 0.025;
      const platform = Math.min(price * 0.025, 500);
      return { originator, platform, receiver: price - originator - platform };
    }
  };

  const formatJobCode = (id) => {
    return `JOP-${id.slice(0, 8).toUpperCase()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${formattedDate} ${formattedTime}`;
  };

  const openInMaps = (address, coordinates = null) => {
    // Try to use coordinates first, fallback to address
    let mapUrl;
    
    if (coordinates && coordinates.lat && coordinates.lng) {
      // Use coordinates for more precise location
      const lat = parseFloat(coordinates.lat);
      const lng = parseFloat(coordinates.lng);
      
      // Detect platform and use appropriate maps URL
      const userAgent = navigator.userAgent;
      if (/iPhone|iPad|iPod/.test(userAgent)) {
        // iOS - Apple Maps
        mapUrl = `maps://?q=${lat},${lng}`;
      } else if (/Android/.test(userAgent)) {
        // Android - Google Maps
        mapUrl = `geo:${lat},${lng}`;
      } else {
        // Desktop - Google Maps web
        mapUrl = `https://maps.google.com/?q=${lat},${lng}`;
      }
    } else if (address) {
      // Fallback to address search
      const encodedAddress = encodeURIComponent(address);
      
      const userAgent = navigator.userAgent;
      if (/iPhone|iPad|iPod/.test(userAgent)) {
        // iOS - Apple Maps
        mapUrl = `maps://?q=${encodedAddress}`;
      } else if (/Android/.test(userAgent)) {
        // Android - Google Maps
        mapUrl = `geo:0,0?q=${encodedAddress}`;
      } else {
        // Desktop - Google Maps web
        mapUrl = `https://maps.google.com/?q=${encodedAddress}`;
      }
    }
    
    if (mapUrl) {
      window.open(mapUrl, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      posted: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Live' },
      accepted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Assigned' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      passed_back: { bg: 'bg-red-100', text: 'text-red-800', label: 'Reassigned' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' }
    };
    return badges[status] || badges.posted;
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      low: 'text-green-600 bg-green-50',
      medium: 'text-yellow-600 bg-yellow-50',
      high: 'text-orange-600 bg-orange-50',
      emergency: 'text-red-600 bg-red-50'
    };
    return colors[urgency] || colors.medium;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Job not found</h2>
          <button 
            onClick={() => navigate('/jobs-list')}
            className="jop-button"
          >
            Back to Jobs List
          </button>
        </div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(job?.status || 'posted');
  const commission = calculateCommission(job?.quoted_price || 0);
  
  // Null-safe permission calculations
  const canAccept = user?.can_receive_jobs && 
                   job?.status === 'posted' && 
                   job?.originator_id !== user?.id && 
                   (job?.payment_status === 'funded' || job?.payment_status === null);
                   
  const canComplete = job?.accepted_by === user?.id && 
                     job?.status === 'accepted' && 
                     (job?.payment_status === 'funded' || job?.payment_status === null);
                     
  const canPassBack = job?.accepted_by === user?.id && 
                     job?.status === 'accepted';
                     
  const canFund = (user?.id === job?.originator_id || user?.is_admin) && 
                 job?.status === 'posted' && 
                 (job?.payment_status === 'unfunded' || !job?.payment_status);
                 
  const canRelease = (user?.id === job?.originator_id || user?.is_admin) && 
                    job?.status === 'completed' && 
                    job?.payment_status === 'held';
                    
  const canCancel = (user?.id === job?.originator_id || user?.is_admin) && 
                   ['posted', 'passed_back'].includes(job?.status) && 
                   !job?.accepted_by;
                    
  const isUnfundedJob = job?.status === 'posted' && 
                       (job?.payment_status === 'unfunded' || !job?.payment_status);
                       
  const showAcceptTooltip = user?.can_receive_jobs && 
                           job?.status === 'posted' && 
                           job?.originator_id !== user?.id && 
                           isUnfundedJob;

  const getPaymentStatusBadge = (paymentStatus) => {
    const badges = {
      unfunded: { bg: 'bg-red-100', text: 'text-red-800', label: 'Unfunded', icon: AlertTriangle },
      funded: { bg: 'bg-green-100', text: 'text-green-800', label: 'Funded', icon: Shield },
      held: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Held in Escrow', icon: Clock },
      released: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Released', icon: CheckCircle },
      disputed: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Disputed', icon: AlertTriangle }
    };
    return badges[paymentStatus] || badges.unfunded;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(-1)} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {formatJobCode(job?.id || '')}
                </h1>
                <p className="text-sm text-gray-600">{job?.title || 'Job Title'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusBadge.bg} ${statusBadge.text}`}>
                {statusBadge.label}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getUrgencyColor(job?.urgency_level || 'normal')}`}>
                {(job?.urgency_level || 'normal').toUpperCase()}
              </span>
              {/* Payment Status Badge - Null-safe */}
              {job && (() => {
                const paymentBadge = getPaymentStatusBadge(job.payment_status || 'unfunded');
                const PaymentIcon = paymentBadge.icon;
                return (
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${paymentBadge.bg} ${paymentBadge.text} flex items-center`}>
                    <PaymentIcon className="h-3 w-3 mr-1" />
                    {paymentBadge.label}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sticky Funding Banner - Only for unfunded jobs */}
        {isUnfundedJob && !fundingBannerDismissed && (
          <div className="mb-6 jop-card p-4 border-l-4 border-blue-500 bg-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900">
                    This job needs funding before it can be accepted
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Secure your job with escrow payment to ensure completion and enable receiver acceptance
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {(user.id === job.originator_id || user.is_admin) && (
                  <button
                    onClick={() => setShowFundingModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Fund Now
                  </button>
                )}
                <button
                  onClick={() => setFundingBannerDismissed(true)}
                  className="text-blue-400 hover:text-blue-600 p-1"
                  title="Dismiss banner"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <div className="jop-card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center mb-2">
                    <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">Location</span>
                  </div>
                  <button
                    onClick={() => openInMaps(job?.location || 'Unknown', job?.gps_coordinates)}
                    className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                    title="Open in Maps"
                  >
                    {job?.location || 'Location not specified'}
                  </button>
                  {job?.location_name && (
                    <div className="mt-1">
                      <p className="text-sm text-gray-600 italic">
                        {job.location_name}
                      </p>
                    </div>
                  )}
                  {job?.detailed_location && job.detailed_location !== job.location && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">Full address:</p>
                      <button
                        onClick={() => openInMaps(job.detailed_location, job.gps_coordinates)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                        title="Open detailed address in Maps"
                      >
                        {job.detailed_location}
                      </button>
                    </div>
                  )}
                  {job?.gps_coordinates && job.gps_coordinates.lat && job.gps_coordinates.lng && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 mb-1">GPS coordinates:</p>
                      <button
                        onClick={() => openInMaps(null, job.gps_coordinates)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left font-mono"
                        title="Open coordinates in Maps"
                      >
                        {job.gps_coordinates.lat}, {job.gps_coordinates.lng}
                      </button>
                    </div>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">Quoted Price</span>
                  </div>
                  <p className="text-gray-700 text-xl font-semibold">${job?.quoted_price || 0}</p>
                </div>
                
                <div className="md:col-span-2">
                  <div className="flex items-center mb-2">
                    <span className="font-medium text-gray-900">Job Specification</span>
                  </div>
                  <p className="text-gray-700">
                    {job?.job_specification || job?.tyre_specification || 'No specification provided'}
                  </p>
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="jop-card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Parties Involved</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center mb-2">
                    <Building className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">Originator</span>
                  </div>
                  <p className="text-gray-700 font-medium">{job?.originator_business || 'Unknown Originator'}</p>
                  {(job?.status === 'accepted' || job?.status === 'completed') && (
                    <p className="text-sm text-gray-500">Contact details available after assignment</p>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <User className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">Receiver</span>
                  </div>
                  {job?.accepted_by_business ? (
                    <>
                      <p className="text-gray-700 font-medium">{job.accepted_by_business}</p>
                      {job?.accepted_at && (
                        <p className="text-sm text-gray-500">
                          Accepted {formatDateTime(job.accepted_at)}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 italic">Not yet assigned</p>
                  )}
                </div>
              </div>
            </div>

            {/* Commission Breakdown */}
            <div className="jop-card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Commission Breakdown</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">${commission?.originator?.toFixed(0) || '0'}</div>
                    <div className="text-sm text-gray-600">Originator Fee</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600">${commission?.platform?.toFixed(0) || '0'}</div>
                    <div className="text-sm text-gray-600">Platform Fee</div>
                    {(job?.quoted_price > 5000 && commission?.platform >= 500) && (
                      <div className="text-xs text-amber-600">Capped at $500</div>
                    )}
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">${commission?.receiver?.toFixed(0) || '0'}</div>
                    <div className="text-sm text-gray-600">Receiver Net</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                  <span className="text-sm text-gray-600">
                    Commission Band: {job.quoted_price < 1000 ? 'Fixed $50 each' : 
                    job.quoted_price <= 5000 ? '10% split (5% each)' : '5% split (2.5% each)'}
                  </span>
                </div>
              </div>
            </div>

            {/* Enhanced Timeline */}
            <div className="jop-card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Job Timeline</h2>
              <div className="space-y-4">
                {timeline.length > 0 ? (
                  timeline.map((entry, index) => {
                    const isFirst = index === 0;
                    const isLast = index === timeline.length - 1;
                    
                    const getTimelineIcon = (action) => {
                      switch (action) {
                        case 'created': return <div className="w-3 h-3 bg-blue-500 rounded-full"></div>;
                        case 'updated': return <div className="w-3 h-3 bg-amber-500 rounded-full"></div>;
                        case 'accepted': return <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>;
                        case 'completed': return <div className="w-3 h-3 bg-green-500 rounded-full"></div>;
                        case 'passed_back': return <div className="w-3 h-3 bg-red-500 rounded-full"></div>;
                        // Payment events
                        case 'payment_created': return <div className="w-3 h-3 bg-blue-600 rounded-full"></div>;
                        case 'job_funded': return <div className="w-3 h-3 bg-green-600 rounded-full"></div>;
                        case 'payment_released': return <div className="w-3 h-3 bg-purple-600 rounded-full"></div>;
                        case 'payment_disputed': return <div className="w-3 h-3 bg-red-600 rounded-full"></div>;
                        case 'payment_refunded': return <div className="w-3 h-3 bg-orange-600 rounded-full"></div>;
                        // Hotline events
                        case 'hotline_started': return <div className="w-3 h-3 bg-pink-500 rounded-full"></div>;
                        case 'hotline_accepted': return <div className="w-3 h-3 bg-pink-600 rounded-full"></div>;
                        default: return <div className="w-3 h-3 bg-gray-400 rounded-full"></div>;
                      }
                    };
                    
                    return (
                      <div key={entry.id} className="flex items-start relative">
                        {/* Timeline line */}
                        {!isLast && (
                          <div className="absolute left-1.5 top-6 w-0.5 h-8 bg-gray-200"></div>
                        )}
                        
                        {/* Timeline dot */}
                        <div className="flex-shrink-0 mr-4 mt-1">
                          {getTimelineIcon(entry.action)}
                        </div>
                        
                        {/* Timeline content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {entry.description}
                            </p>
                            <span className="text-xs text-gray-500">
                              {formatDateTime(entry.timestamp)}
                            </span>
                          </div>
                          
                          <p className="text-xs text-gray-600 mt-1">
                            by {entry.user_business}
                          </p>
                          
                          {/* Show changes for admin users */}
                          {user.is_admin && entry.changes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                              <strong>Changes made:</strong>
                              <ul className="mt-1 space-y-1">
                                {Object.entries(entry.changes).map(([field, change]) => (
                                  <li key={field} className="text-gray-600">
                                    <span className="font-medium">{field}:</span> 
                                    <span className="text-red-600"> {
                                      typeof change.old === 'object' 
                                        ? JSON.stringify(change.old) 
                                        : String(change.old || 'none')
                                    }</span> →
                                    <span className="text-green-600"> {
                                      typeof change.new === 'object' 
                                        ? JSON.stringify(change.new) 
                                        : String(change.new || 'none')
                                    }</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-4"></div>
                    <div>
                      <p className="font-medium text-gray-900">Job Posted</p>
                      <p className="text-sm text-gray-600">{formatDateTime(job.created_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SMS Logs (Admin only) */}
            {user.is_admin && notifications.length > 0 && (
              <div className="jop-card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">SMS Notifications</h2>
                <div className="space-y-3">
                  {notifications.map((notification) => (
                    <div key={notification.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            To: {notification.recipient_phone}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          notification.status === 'sent' ? 'bg-green-100 text-green-800' :
                          notification.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {notification.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {formatDateTime(notification.sent_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-3">
                {/* Emergency Breakdown - For urgent/after_hours jobs */}
                {(job.urgency_level === 'asap' || job.urgency_level === 'after_hours' || job.urgency_level === 'emergency') && (
                  <div className="mb-4">
                    <EmergencyCallButton 
                      jobId={job.id}
                      variant="compact"
                      className="w-full"
                    />
                  </div>
                )}
                
                {/* Fund Job - Enhanced visibility for originators and admins */}
                {canFund && (
                  <button
                    onClick={() => setShowFundingModal(true)}
                    className="jop-button w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    {user.is_admin && user.id !== job.originator_id 
                      ? 'Fund on Behalf of Originator' 
                      : 'Fund Job with Escrow'
                    }
                  </button>
                )}
                
                {/* Enhanced Accept Job with Tooltip for Unfunded Jobs */}
                {canAccept && (
                  <button
                    onClick={handleAcceptJob}
                    className="jop-button w-full flex items-center justify-center"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept Job
                  </button>
                )}
                
                {/* Disabled Accept Button with Tooltip for Unfunded Jobs */}
                {showAcceptTooltip && (
                  <div className="relative group">
                    <button
                      disabled={true}
                      className="jop-button w-full flex items-center justify-center opacity-50 cursor-not-allowed"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Accept Job (Funding Required)
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      Originator must fund the job before it can be accepted
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                )}
                
                {canComplete && (
                  <button
                    onClick={handleCompleteJob}
                    className="jop-button w-full flex items-center justify-center"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark Complete
                  </button>
                )}
                
                {/* Release Payment - For completed jobs with held funds */}
                {canRelease && (
                  <button
                    onClick={handleReleasePayment}
                    className="jop-button w-full flex items-center justify-center bg-green-600 hover:bg-green-700"
                  >
                    <DollarSign className="h-4 w-4 mr-2" />
                    Release Escrow Payment
                  </button>
                )}
                
                {canPassBack && (
                  <button
                    onClick={() => setShowPassBackModal(true)}
                    className="jop-button-secondary w-full flex items-center justify-center"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Pass Back
                  </button>
                )}

                {/* Cancel Job - Only for originators/admins on unassigned jobs */}
                {canCancel && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="jop-button-secondary w-full flex items-center justify-center border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cancel Job
                  </button>
                )}

                {/* Edit Job - Only for originators on live/passed-back jobs */}
                {user.id === job.originator_id && (job.status === 'posted' || job.status === 'passed_back') && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="jop-button-secondary w-full flex items-center justify-center"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Job
                  </button>
                )}

                {/* Show locked message for accepted jobs */}
                {user.id === job.originator_id && job.status === 'accepted' && (
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    <Lock className="h-4 w-4 mr-2" />
                    Job locked once accepted. Contact receiver if changes needed.
                  </div>
                )}

                {/* Support - Always visible */}
                <button
                  onClick={() => setShowSupportModal(true)}
                  className="jop-button-secondary w-full flex items-center justify-center"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Contact Support
                </button>
              </div>
            </div>

            {/* Job Stats */}
            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Job Code</span>
                  <span className="text-sm font-medium text-gray-900">{formatJobCode(job.id)}</span>
                </div>
                
                {/* Invoice Number Fields - Role-based visibility */}
                {(user.id === job.originator_id || user.id === job.accepted_by || user.is_admin) && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Originator Invoice Number</span>
                      <span className="text-sm font-medium text-gray-900">
                        {job.originator_invoice_number || 'Not set'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Receiver Invoice Number</span>
                      <span className="text-sm font-medium text-gray-900">
                        {job.receiver_invoice_number || 'Not set'}
                      </span>
                    </div>
                  </>
                )}
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Posted</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(job.created_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Urgency</span>
                  <span className={`text-sm font-medium ${job.urgency_level === 'emergency' ? 'text-red-600' : 
                    job.urgency_level === 'high' ? 'text-orange-600' :
                    job.urgency_level === 'medium' ? 'text-yellow-600' : 'text-green-600'}`}>
                    {job.urgency_level.charAt(0).toUpperCase() + job.urgency_level.slice(1)}
                  </span>
                </div>
                {job.accepted_at && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Response Time</span>
                    <span className="text-sm font-medium text-gray-900">
                      {Math.round((new Date(job.accepted_at) - new Date(job.created_at)) / (1000 * 60))} min
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Edit Job Modal */}
      <UnifiedEditJobModal
        job={job}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={fetchJobDetails}
      />

      {/* Pass Back Modal */}
      {showPassBackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="jop-card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pass Back Job</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for passing back this job. It will be re-sent to all available receivers.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for passing back
              </label>
              <textarea
                value={passBackReason}
                onChange={(e) => setPassBackReason(e.target.value)}
                className="jop-input resize-none"
                rows="3"
                placeholder="e.g., No stock available, truck breakdown, etc."
                required
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handlePassBackJob}
                className="jop-button flex-1"
              >
                Pass Back Job
              </button>
              <button
                onClick={() => {
                  setShowPassBackModal(false);
                  setPassBackReason('');
                }}
                className="jop-button-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Modal */}
      <SupportModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)}
        jobId={job?.id}
      />

      {/* Job Funding Modal */}
      <JobFundingModal
        job={job}
        isOpen={showFundingModal}
        onClose={() => setShowFundingModal(false)}
        onSuccess={fetchJobDetails}
      />

      {/* Cancel Job Modal */}
      <CancelJobModal
        job={job}
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSuccess={fetchJobDetails}
      />
    </div>
  );
};

export default JobDetail;